import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import "./Quality.css";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import {
    Kpi, MockBanner, RingKpi, SEVERITY, SOURCES, STAGES, SevChip, SourceChip, StagePill,
    fmtShort, hasPerm, stageIndex,
} from "./qualityUi";
import RaiseBugModal from "./RaiseBugModal";

/**
 * Production Bugs — every issue that reached a hospital, from any source.
 *
 * Laid out like the Sprint ticket table (KPI tiles, toolbar, table card) so it
 * reads as part of the same workflow. Filters go to the API; the KPI tiles are
 * counted from the unfiltered list so they don't jump around while filtering.
 */
export default function ProductionBugs() {
    const navigate = useNavigate();
    const [all, setAll] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [f, setF] = useState({ source: "", severity: "", stage: "open", q: "" });
    const [raising, setRaising] = useState(false);
    const [confirming, setConfirming] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const canDelete = hasPerm("Delete Ticket");

    const removeBug = async (b) => {
        setDeletingId(b.id);
        try {
            const res = await qualityApi.deleteBug(b.id);
            showSuccessToast(res.message || `${b.code} deleted.`);
            setConfirming(null);
            await load();
        } catch (e) {
            setConfirming(null);
            showErrorToast(e.message || "Could not delete the bug.");
        } finally {
            setDeletingId(null);
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const [filtered, everything] = await Promise.all([qualityApi.getBugs(f), qualityApi.getBugs({})]);
            setRows(filtered.data || []);
            setAll(everything.data || []);
        } catch (e) {
            setErr(e.message || "Could not load production bugs.");
        } finally {
            setLoading(false);
        }
    }, [f]);

    useEffect(() => {
        const t = setTimeout(load, f.q ? 250 : 0);
        return () => clearTimeout(t);
    }, [load, f.q]);

    const k = useMemo(() => {
        const open = all.filter((b) => b.stage !== "closed");
        return {
            total: all.length,
            closedPct: all.length ? ((all.length - open.length) / all.length) * 100 : 0,
            untraced: open.filter((b) => stageIndex(b.stage) < 2).length,
            rca: open.filter((b) => b.stage === "rca").length,
            fixing: open.filter((b) => stageIndex(b.stage) >= 4).length,
            critical: open.filter((b) => b.severity === "P0" || b.severity === "P1").length,
            open: open.length,
        };
    }, [all]);

    const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
    const filterCls = (v) => `qa-filter${v ? " qa-filter--on" : ""}`;
    const share = (n) => (k.open ? (n / k.open) * 100 : 0);

    return (
        <div className="qa-page">
            <MockBanner />
            <div className="qa-head">
                <h1>Production Bugs</h1>
                <span className="qa-sp" />
                {hasPerm("Log Production Bug") && (
                    <button className="qa-btn qa-btn--primary" onClick={() => setRaising(true)}>
                        <FiPlus size={12} /> Log production bug
                    </button>
                )}
            </div>

            <div className="qa-kpis">
                <RingKpi percent={k.closedPct} value={k.total} label="Total bugs (closed %)" />
                <Kpi value={k.untraced} label="Not traced yet" color="var(--qa-orange)" fill={share(k.untraced)} />
                <Kpi value={k.rca} label="In root cause" color="var(--qa-blue)" fill={share(k.rca)} />
                <Kpi value={k.fixing} label="Being fixed" color="var(--qa-green)" fill={share(k.fixing)} />
                <Kpi value={k.critical} label="P0 / P1 open" color="var(--qa-red)" fill={share(k.critical)} />
            </div>

            <div className="qa-toolbar">
                <span className="qa-toolbar__count">{loading ? "Loading…" : `${rows.length} bug${rows.length === 1 ? "" : "s"}`}</span>
                <select id="qa-bugs-source" className={filterCls(f.source)} value={f.source} onChange={(e) => set({ source: e.target.value })} aria-label="Source">
                    <option value="">Source</option>
                    {Object.entries(SOURCES).map(([key, v]) => <option key={key} value={key}>{v.label}</option>)}
                </select>
                <select id="qa-bugs-sev" className={filterCls(f.severity)} value={f.severity} onChange={(e) => set({ severity: e.target.value })} aria-label="Severity">
                    <option value="">Severity</option>
                    {Object.keys(SEVERITY).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select id="qa-bugs-stage" className={filterCls(f.stage)} value={f.stage} onChange={(e) => set({ stage: e.target.value })} aria-label="Stage">
                    <option value="">All stages</option>
                    <option value="open">All open</option>
                    {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <input
                    id="qa-bugs-q" className="qa-search" placeholder="Search…" value={f.q}
                    onChange={(e) => set({ q: e.target.value })} aria-label="Search bugs"
                />
            </div>

            <div className="qa-card">
                {err ? <div className="qa-empty">{err}</div> : (
                    <table className="qa-tbl">
                        <thead>
                            <tr>
                                <th>Bug No</th><th>Description</th><th>Source</th><th>Hospital</th><th>Module</th>
                                <th>Severity</th><th>Stage</th><th>Release · Ticket</th><th>Reported</th>
                                {canDelete && <th aria-label="Actions" />}
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={canDelete ? 10 : 9} className="qa-empty">No bugs match these filters.</td></tr>
                            )}
                            {rows.map((b) => (
                                <tr key={b.id} className="qa-click" onClick={() => navigate(`/quality/bugs/${b.id}`)}>
                                    <td className="qa-code">{b.code}</td>
                                    <td><div className="qa-trunc">{b.title}</div></td>
                                    <td><SourceChip value={b.source} /></td>
                                    <td className="qa-nw">{b.org_name}</td>
                                    <td>{b.module}</td>
                                    <td><SevChip value={b.severity} /></td>
                                    <td><StagePill value={b.stage} /></td>
                                    <td className="qa-nw">
                                        {b.release
                                            ? <><span className="qa-mono">{b.release.version}</span> · {b.ticket_code}</>
                                            : b.untraced_reason
                                                ? <span className="qa-chip qa-chip--amber">Untraced</span>
                                                : <span className="qa-faint">—</span>}
                                    </td>
                                    <td className="qa-nw qa-muted">{fmtShort(b.reported_at)}</td>
                                    {canDelete && (
                                        // The row opens the bug, so clicks in here must not bubble.
                                        <td className="qa-nw" onClick={(e) => e.stopPropagation()}>
                                            {confirming === b.id ? (
                                                <div className="qa-rowblock">
                                                    <span className="qa-hint">Delete {b.code}?</span>
                                                    <button className="qa-btn qa-btn--sm qa-btn--danger" disabled={deletingId === b.id} onClick={() => removeBug(b)}>
                                                        {deletingId === b.id ? "Deleting…" : "Delete"}
                                                    </button>
                                                    <button className="qa-btn qa-btn--sm" disabled={deletingId === b.id} onClick={() => setConfirming(null)}>Keep</button>
                                                </div>
                                            ) : (
                                                <button className="qa-btn qa-btn--sm qa-btn--danger" onClick={() => setConfirming(b.id)}>Delete</button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {raising && (
                <RaiseBugModal
                    onClose={() => setRaising(false)}
                    onCreated={(b) => navigate(`/quality/bugs/${b.id}`, { state: { tab: "trace" } })}
                />
            )}
        </div>
    );
}
